import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

function SecureMarkdownRenderer({ children, className, style }) {
  // Security: Custom link renderer that blocks relative URLs
  const linkRenderer = ({ node, ...props }) => {
    const { href, children } = props;
    
    // Block relative URLs and javascript: URIs for security
    // eslint-disable-next-line no-script-url
    if (!href || href.startsWith('javascript:') || href.startsWith('data:')) {
      return <span style={{ color: '#ef4444' }}>[Blocked Link: {children}]</span>;
    }
    
    // Only allow absolute URLs (http/https)
    if (!href.startsWith('http://') && !href.startsWith('https://')) {
      return <span style={{ color: '#ef4444' }}>[Blocked Relative Link: {children}]</span>;
    }
    
    // Render safe external links
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: isDark ? '#60a5fa' : '#3b82f6', textDecoration: 'underline' }}
      >
        {children}
      </a>
    );
  };

  // Security: Custom image renderer that blocks relative URLs
  const imageRenderer = ({ node, ...props }) => {
    const { src, alt } = props;
    
    // Block relative URLs and javascript: URIs for security
    // eslint-disable-next-line no-script-url
    if (!src || src.startsWith('javascript:') || src.startsWith('data:')) {
      return <span style={{ color: '#ef4444' }}>[Blocked Image: {alt || 'image'}]</span>;
    }
    
    // Only allow absolute URLs (http/https)
    if (!src.startsWith('http://') && !src.startsWith('https://')) {
      return <span style={{ color: '#ef4444' }}>[Blocked Relative Image: {alt || 'image'}]</span>;
    }
    
    // Render safe external images
    return (
      <img
        src={src}
        alt={alt}
        style={{ maxWidth: '100%', height: 'auto' }}
      />
    );
  };

  const components = {
    a: linkRenderer,
    img: imageRenderer,
    // Style other markdown elements
    p: ({ children }) => (
      <p style={{ margin: '0.5rem 0', lineHeight: '1.5' }}>{children}</p>
    ),
    h1: ({ children }) => (
      <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: '1rem 0 0.5rem 0' }}>{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: '1rem 0 0.5rem 0' }}>{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', margin: '1rem 0 0.5rem 0' }}>{children}</h3>
    ),
    ul: ({ children }) => (
      <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>{children}</ol>
    ),
    li: ({ children }) => (
      <li style={{ margin: '0.25rem 0' }}>{children}</li>
    ),
    code: ({ node, inline, className, children, ...props }) => {
      const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      
      // More robust inline detection - check multiple conditions
      const isInline = inline || 
                      (node && node.position && 
                       node.position.start.line === node.position.end.line) ||
                      !className?.includes('language-');
      
      if (isInline) {
        return (
          <code style={{
            backgroundColor: isDark ? '#374151' : '#f3f4f6',
            color: isDark ? '#e5e7eb' : '#374151',
            padding: '0.125rem 0.25rem',
            borderRadius: '4px',
            fontSize: '0.875rem',
            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
            border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db'
          }}>
            {children}
          </code>
        );
      }
      
      // Code block rendering
      return (
        <pre style={{
          backgroundColor: isDark ? '#1f2937' : '#f3f4f6',
          color: isDark ? '#e5e7eb' : '#374151',
          padding: '1rem',
          borderRadius: '8px',
          overflow: 'auto',
          fontSize: '0.875rem',
          fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
          border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb',
          margin: '1rem 0'
        }}>
          <code className={className} {...props}>
            {children}
          </code>
        </pre>
      );
    },
    blockquote: ({ children }) => {
      const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return (
        <blockquote style={{
          borderLeft: isDark ? '4px solid #6b7280' : '4px solid #e5e7eb',
          paddingLeft: '1rem',
          margin: '1rem 0',
          fontStyle: 'italic',
          color: isDark ? '#9ca3af' : '#6b7280'
        }}>
          {children}
        </blockquote>
      );
    },
    strong: ({ children }) => (
      <strong style={{ fontWeight: 'bold' }}>{children}</strong>
    ),
    em: ({ children }) => (
      <em style={{ fontStyle: 'italic' }}>{children}</em>
    ),
    pre: ({ children, ...props }) => {
      // Handle pre-wrapped code blocks
      const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return (
        <pre style={{
          backgroundColor: isDark ? '#1f2937' : '#f3f4f6',
          color: isDark ? '#e5e7eb' : '#374151',
          padding: '1rem',
          borderRadius: '8px',
          overflow: 'auto',
          fontSize: '0.875rem',
          fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
          border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb',
          margin: '1rem 0'
        }} {...props}>
          {children}
        </pre>
      );
    },
  };

  return (
    <div className={className} style={style}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={components}
        skipHtml={true} // Skip HTML for security
      >
        {children || ''}
      </ReactMarkdown>
    </div>
  );
}

export default SecureMarkdownRenderer; 